import { useEffect } from 'react';

import NavigationPanelContextProvider, { useNavigationPanelContext } from '@app/components/NavigationPanelProvider';
import commentsActions from '@app/features/comments/store/actions';
import BranchSelect from '@app/features/project/components/BranchSelect';
import useDispatch from '@app/hooks/useDispatch';
import { navigateToSearch } from '@app/utils/location';
import { badgeActions } from '../../store/badges/reducers';
import NavigationPanel from './NavigationPanel';
import Breadcrumbs from './components/Breadcrumbs';
import ExploreDesignPlugins from './components/ExploreDesignPlugins';
import ProjectNavigation from './components/ProjectNavigation/ProjectNavigation';
import useProjectNavigation from './hooks/useProjectNavigation/useProjectNavigation';

const { getBadgeStatus } = badgeActions;

type ProjectHeaderContainerProps = {
    activeItem: string;
    sticky?: boolean;
};

const NavigationPanelContainer = ({ activeItem, sticky = false }: ProjectHeaderContainerProps) => {
    const dispatch = useDispatch();

    const { project, branch, userRights } = useNavigationPanelContext();
    const navigation = useProjectNavigation();

    useEffect(() => {
        dispatch(
            getBadgeStatus({
                projectId: project.id,
                branchId: branch.branchId,
            }),
        );
    }, [project.id, branch.branchId, dispatch]);

    const handleBranchSelected = (branchName: string) => {
        dispatch(commentsActions.closeSidebar());

        const branchUrl = `?view=${project.view ?? ''}&branch=${branchName || ''}&search=${project.search ?? ''}`;
        navigateToSearch(branchUrl);
    };

    const breadcrumbsItems = [
        {
            name: 'Projects',
            link: '/projects',
        },
        {
            name: project.name,
            link: `/project/${project.id}/`,
        },
    ];

    return (
        <NavigationPanel sticky={sticky}>
            <NavigationPanel.Header>
                <Breadcrumbs items={breadcrumbsItems} />
                {project.isBranchingEnabled && (
                    <BranchSelect
                        projectId={project.id}
                        currentBranchName={branch.branchName}
                        hasAdminRightsCreateBranch={userRights.adminRights.createBranches}
                        hasAdminRightsMergeBranch={userRights.adminRights.mergeBranches}
                        branches={branch}
                        onBranchCreated={() => {
                            dispatch(commentsActions.closeSidebar());
                        }}
                        onSelected={handleBranchSelected}
                        masterOnlyPage={activeItem === 'workflows'}
                    />
                )}
            </NavigationPanel.Header>

            <NavigationPanel.Tabs>
                <ProjectNavigation navigation={navigation} activeItem={activeItem} />
                <ExploreDesignPlugins />
            </NavigationPanel.Tabs>
        </NavigationPanel>
    );
};

const NavigationPanelWithContext = (props: ProjectHeaderContainerProps) => (
    <NavigationPanelContextProvider>
        <NavigationPanelContainer {...props} />
    </NavigationPanelContextProvider>
);

export default NavigationPanelWithContext;
